import { ApolloQueryResult } from '@apollo/client';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { defaultTo } from 'lodash';
import capitalize from 'lodash/capitalize';
import lowerCase from 'lodash/lowerCase';
import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';
import Button from '@atlaskit/button/standard-button';
import {
  CopyableText,
  NewBillDate,
  OpenSidebarPayload,
  SummarySectionV2,
  formattedV2,
  getCASSIBaseURL,
  isAccess,
  useFeatureToggle,
  useModalDialog,
  useSidebar,
  useWarningModalDialog,
} from '@css-xp/base';
import { TransactionAccountContext } from '@css-xp/base/common-context';
import {
  BillingStatus,
  EntitlementDetails,
  InvoiceGroup,
  LinkedDestinationEntitlement,
} from '@css-xp/graphtypes/src/generated-types-new';
import { UFOUiAction, ufoHandleButtonClick } from '@atlassian/temp-observability';
import {
  CLOUD_SITE_PATH,
  CLOUD_SITE_PATH_2,
  NEW_DATE_FORMAT,
  NEW_DESIGN_DATE_FORMAT,
  Permissions,
  SourceSystem,
} from '@atlassian/cssxp-typescommon';
import {
  getNextBillOrEndDate,
  isDeletedEntitlement,
  isOfferingArchived,
  isOfferingAtNotice,
  isAnnualTermedSubscription,
} from '@atlassian/cssxp-utils-entitlement';
import {
  ENABLE_ANNUAL_TERMS,
  ENABLE_CLOUD_SITE_AC_REDIRECTION,
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_SANDBOX_ENTITLEMENT,
  ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS,
} from '@atlassian/cssxp-utils-generic';
import { isBilledElsewhereProduct, isNpfProduct, isStatuspage } from '@atlassian/cssxp-utils-product';
import { PREDICATES } from '../../common/constants';
import {
  fetchWarningProps,
  getChangeBillDateSidebarPayload,
  validateFreeSubscriptions,
  validateSubscriptionDetails,
} from '../../entitlement-page';
import { SIDEBAR } from '../../entitlement-page/src/components/entitlement-page';
import { PricingSection } from './template/sections';
import { AnalyticsUiAction, AnalyticsUiSubject, analytics, AnalyticsTrackSubject } from '@atlassian/temp-analytics';
import './entitlement-summary-utils.scss';

// Same implementation is present in backend also, please change there also
// packages/common/ans/utils/entitlement/src/entitlement-utils.ts
export const isStatusPageTrial = (props: EntitlementDetails | LinkedDestinationEntitlement) => {
  return isStatuspage(props.productId) && props?.edition?.toLowerCase().includes('trial');
};

// Same implementation is present in backend also, please change there also
// packages/common/ans/utils/entitlement/src/entitlement-utils.ts
export const isTrialEntitlement = (props: EntitlementDetails | LinkedDestinationEntitlement, isStatusPage: boolean) => {
  return (
    isStatusPageTrial(props) ||
    (!isStatusPage &&
    !props.upgradeTrial &&
    props?.subscriptionTimestamps?.firstTrialStartTimestamp &&
    props?.subscriptionTimestamps?.lastTrialEndTimestamp
      ? moment.utc(props?.subscriptionTimestamps?.lastTrialEndTimestamp).isSameOrAfter(moment.utc())
      : false)
  );
};

export const isActiveEntitlement = (entitlement: EntitlementDetails) => {
  return lowerCase(entitlement.billingStatus ?? '') === lowerCase(BillingStatus.ACTIVE);
};

export const getFormattedDate = (date: string) => {
  return <formattedV2.FormattedDate dateFormat={NEW_DATE_FORMAT} date={date} />;
};

const getBillDateLabel = (entitlement: EntitlementDetails, isAnnualTermEnabled?: boolean) => {
  if (isAnnualTermedSubscription(entitlement, isAnnualTermEnabled)) {
    return 'End date';
  }
  return entitlement?.isApp && (isOfferingArchived(entitlement) || isDeletedEntitlement(entitlement))
    ? 'Subscription end date'
    : 'Next bill date';
};

export const EntitlementsCommonColumns = ({
  entitlement,
  linkedEntitlements,
  refetch,
}: {
  entitlement: EntitlementDetails;
  linkedEntitlements?: LinkedDestinationEntitlement[];
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}) => {
  const { cloudSiteHostname, migrated } = entitlement;
  const [isEnabled] = useFeatureToggle();
  const cassiBaseURL = getCASSIBaseURL();
  const isAnnualTermEnabled = isEnabled(ENABLE_ANNUAL_TERMS);
  const sitePath = migrated?.toUpperCase() === SourceSystem.CCP ? CLOUD_SITE_PATH_2 : CLOUD_SITE_PATH;
  return (
    <>
      <SummarySectionV2.SummaryGridCalloutItem testId="summary-cloud-site">
        <div className="billing-cycle title-height">
          <label>Cloud URL</label>
          <div className="entitlement-details-column copy-link">
            {cloudSiteHostname ? (
              isEnabled(ENABLE_CLOUD_SITE_AC_REDIRECTION) ? (
                <CopyableText duration={0}>
                  <Link to={`${sitePath}/${entitlement.cloudSiteId}`}>{cloudSiteHostname}</Link>
                </CopyableText>
              ) : (
                <CopyableText duration={0}>
                  <Link
                    to={{ pathname: `${cassiBaseURL}${CLOUD_SITE_PATH}/${entitlement.cloudSiteId}` }}
                    target="_blank"
                  >
                    {cloudSiteHostname}
                  </Link>
                </CopyableText>
              )
            ) : (
              <span data-testid="empty-cloud-site" className="data-unavailable">
                N/A
              </span>
            )}
          </div>
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>

      <SummarySectionV2.SummaryGridCalloutItem testId="summary-billing-cycle">
        <div className="billing-cycle title-height">
          <label>Billing cycle</label>
          {getBillingCycle(entitlement, isAnnualTermEnabled)}
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>

      <SummarySectionV2.SummaryGridCalloutItem testId="summary-next-bill-date">
        <div className="next-bill billing-cycle title-height" data-testid="summary-next-billing-date-label">
          <label>{getBillDateLabel(entitlement, isAnnualTermEnabled)} </label>
          <NextBillDate entitlement={entitlement} refetch={refetch} />
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>
      <SummarySectionV2.SummaryGridCalloutItem testId="summary-offering-name">
        <div className="billing-plan billing-cycle title-height">
          <label>Plan</label>
          <GetOfferingName entitlement={entitlement} refetch={refetch} />
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>
      <PricingSection entitlement={entitlement} linkedEntitlements={linkedEntitlements as []} refetch={refetch} />
    </>
  );
};
export const getBillingCycle = (props: EntitlementDetails, isAnnualTermEnabled?: boolean) => {
  const { billingFrequency, latestPeriodStartDate } = props;
  const isActive = isActiveEntitlement(props);
  const isStatusPage = isStatuspage(props.productId);
  const isTrial = isTrialEntitlement(props, isStatusPage);
  const nextBillOrEndDate = getNextBillOrEndDate(props, isAnnualTermEnabled);

  return (
    <div className="entitlement-details-column">
      {isActive && billingFrequency && billingFrequency !== 'OTHER' ? (
        <>
          <span>{`${capitalize(billingFrequency)} `}</span>
          <br />

          {!isTrial && latestPeriodStartDate && nextBillOrEndDate && (
            <CopyableText>
              <formattedV2.FormattedDateRange start={latestPeriodStartDate} end={nextBillOrEndDate} />
            </CopyableText>
          )}
        </>
      ) : (
        <span data-testid="empty-billing-cycle" className="data-unavailable">
          N/A
        </span>
      )}
    </div>
  );
};

export const getTrialDate = (date?: string) => {
  return (
    <div>
      {date ? (
        <CopyableText>
          <formattedV2.FormattedDate dateFormat={NEW_DESIGN_DATE_FORMAT} date={date} />
        </CopyableText>
      ) : (
        <span data-testid="empty-trial-date" className="data-unavailable">
          N/A
        </span>
      )}
    </div>
  );
};

export const NextBillDate = ({
  entitlement,
  refetch,
  isBillDateChangeEnabled = true,
}: {
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  isBillDateChangeEnabled?: boolean;
}) => {
  const { billingStatus } = entitlement;
  const isActive = lowerCase(billingStatus ?? '') === lowerCase(BillingStatus.ACTIVE);
  const { openSidebar } = useSidebar();
  const [isEnabled] = useFeatureToggle();
  const { openWarningModalDialog, closeWarningModalDialog } = useWarningModalDialog();
  const isAnnualTermEnabled = isEnabled(ENABLE_ANNUAL_TERMS);
  const nextBillOrEndDate = getNextBillOrEndDate(entitlement, isAnnualTermEnabled);

  const isSandbox = isEnabled(ENABLE_SANDBOX_ENTITLEMENT) && entitlement?.isSandbox;
  const isFree = validateFreeSubscriptions(entitlement);
  const productIsNPF = isNpfProduct(entitlement?.productId);
  const isEntRelationFeatureEnable = isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE);
  const isWarningModalDialogEnable = isEnabled(ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS);

  const linkedEntsTableVisible =
    isEntRelationFeatureEnable &&
    (!!entitlement?.relatesFromEntitlements?.length || !!entitlement?.relatesToEntitlements?.length);

  const warningModalDailogVisible = linkedEntsTableVisible && isWarningModalDialogEnable;

  let changeBillDatePayload = getChangeBillDateSidebarPayload(entitlement, refetch);
  changeBillDatePayload = {
    ...changeBillDatePayload,
    payload: {
      ...changeBillDatePayload.payload,
      billingFrequency: entitlement.billingFrequency,
    },
  };

  const handleConfirmWarningDailog = (sidebarPayload: OpenSidebarPayload) => {
    closeWarningModalDialog();
    openSidebar(sidebarPayload);
  };

  const handleBillDateChange = () => {
    ufoHandleButtonClick(UFOUiAction.CHANGE_BILL_DATE);
    analytics.sendUIEvent({
      action: AnalyticsUiAction.CLICKED,
      actionSubject: AnalyticsUiSubject.BUTTON,
      actionSubjectId: AnalyticsTrackSubject.CHANGE_BILL_DATE,
    });
    if (warningModalDailogVisible) {
      openWarningModalDialog({
        ...fetchWarningProps(PREDICATES.SET_BILL_DATE),
        onConfirm: () =>
          handleConfirmWarningDailog({
            ...changeBillDatePayload,
            sidebarType: changeBillDatePayload.sidebar,
          }),
      });
    } else {
      openSidebar({
        ...changeBillDatePayload,
        sidebarType: changeBillDatePayload.sidebar,
      });
    }
  };
  const billedElsewhere = isBilledElsewhereProduct(entitlement?.productId);

  return (
    <div className="entitlement-details-column">
      {nextBillOrEndDate && isActive ? (
        <CopyableText>
          <formattedV2.FormattedDate dateFormat={NEW_DESIGN_DATE_FORMAT} date={nextBillOrEndDate} />
        </CopyableText>
      ) : (
        <div className="data-unavailable">N/A</div>
      )}
      {isActive && !isAnnualTermedSubscription(entitlement, isAnnualTermEnabled) && (
        <NewBillDate entitlement={entitlement} />
      )}
      {isBillDateChangeEnabled &&
        isActive &&
        !isFree &&
        !isSandbox &&
        !isAnnualTermedSubscription(entitlement, isAnnualTermEnabled) &&
        !isDeletedEntitlement(entitlement) &&
        !(entitlement?.isApp && (isOfferingArchived(entitlement) || isOfferingAtNotice(entitlement))) &&
        isAccess(Permissions.SUBSCRIPTION_ENTITLEMENT_MANAGE) &&
        !productIsNPF &&
        !billedElsewhere && (
          <Button
            testId="change_bill_date"
            aria-label="Change bill date"
            appearance="link"
            className="inner-text"
            onClick={() => handleBillDateChange()}
          >
            Change
          </Button>
        )}
    </div>
  );
};

export const GetOfferingName = ({
  entitlement,
  refetch,
  isChangeEnabled = true,
}: {
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  isChangeEnabled?: boolean;
}) => {
  const { openModalDialog } = useModalDialog();
  const { openSidebar } = useSidebar();
  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };
  const transactionAccountContext = React.useContext(TransactionAccountContext);
  const { offering } = entitlement;
  const isActive = lowerCase(entitlement?.billingStatus ?? '') === lowerCase(BillingStatus.ACTIVE);
  const { data, getTransactionAccount } = transactionAccountContext;
  const txaccount = getTransactionAccount(data);
  const availableInvoiceGroups = defaultTo(txaccount?.availableInvoiceGroups?.nodes, []) as InvoiceGroup[];
  const [isEnabled] = useFeatureToggle();
  const isUpgradeTrialOffering = entitlement?.upgradeTrial && !isStatuspage(entitlement?.productId);

  const isSandbox = isEnabled(ENABLE_SANDBOX_ENTITLEMENT) && entitlement?.isSandbox;
  return (
    <div className="entitlement-details-column">
      <CopyableText>
        <span>{offering?.name}</span>
      </CopyableText>
      {isChangeEnabled &&
        isActive &&
        !isSandbox &&
        !isDeletedEntitlement(entitlement) &&
        !isNpfProduct(entitlement?.productId) &&
        !isBilledElsewhereProduct(entitlement?.productId) &&
        isAccess(Permissions.SUBSCRIPTION_WRITE) && (
          <Button
            data-testId="change-offered-plan"
            aria-label="Change plan"
            appearance="link"
            className="inner-text"
            onClick={() => {
              ufoHandleButtonClick(UFOUiAction.CHANGE_PLAN);
              analytics.sendUIEvent({
                action: AnalyticsUiAction.CLICKED,
                actionSubject: AnalyticsUiSubject.BUTTON,
                actionSubjectId: AnalyticsTrackSubject.CHANGE_PLAN,
              });
              validateSubscriptionDetails(entitlement, availableInvoiceGroups, txaccount)
                ? handleSidebarAction({
                    sidebarType: SIDEBAR.STATUSPAGE_CHANGE_PLAN,
                    payload: {
                      entitlementDetails: entitlement,
                      transactionAccountContext,
                      refetch,
                    },
                    sidebarOptions: {
                      isOverlay: true,
                      sidebarWidth: '600px',
                    },
                  })
                : openModalDialog();
            }}
          >
            Change
          </Button>
        )}
    </div>
  );
};

/**
 * This logic applies only to TRIAL Ent of JIRA & Confluence .
 */
export const getTotalDaysInTrial = (subscriptions: EntitlementDetails['subscription'], currentOfferingId: string) => {
  if (!subscriptions?.length) return 0;
  let totalDaysInTrial = 0;

  const matchingSubcriptions = subscriptions[0]?.subscriptionPeriod?.filter((subscription) => {
    if (subscription) {
      return subscription.offeringKey === currentOfferingId;
    }
  });

  matchingSubcriptions?.forEach((subscription) => {
    if (subscription && subscription.trial && subscription.trial.endTimestamp && subscription.trial.startTimestamp) {
      const differenceInDay = differenceInCalendarDays(
        parseISO(subscription.trial.endTimestamp),
        parseISO(subscription.trial.startTimestamp)
      );

      if (differenceInDay > totalDaysInTrial) {
        totalDaysInTrial = differenceInDay;
      }
    }
  });
  return totalDaysInTrial;
};

export const ViewDeletedProductDetails = ({ entitlement }: { entitlement: EntitlementDetails }) => {
  const { openSidebar } = useSidebar();
  return (
    <>
      <div className="entitlement-deleted-product-details">
        <Button
          aria-label="View details"
          appearance="link"
          className="inner-text"
          onClick={() =>
            openSidebar({
              sidebarType: SIDEBAR.VIEW_DELETED_PRODUCT_DETAILS,
              payload: {
                entitlement,
              },
              sidebarOptions: {
                isOverlay: true,
                sidebarWidth: '600px',
              },
            })
          }
        >
          View details
        </Button>
      </div>
    </>
  );
};
